// Shared layout for /student/competition/[id]/* routes.
//
// Owns: shared data fetching (competition + team + deliverables + submissions
// + statuses + leaderboard + announcements + browse teams), realtime
// subscriptions for the team-unread badge, the StudentProgramShell chrome
// (forest sidebar + persistent header), and the Context that exposes shared
// state to the child tab routes.
//
// Why this exists: keeping the shell mounted across sibling-route navigation
// kills the 1.5s "0% → 33% case-progress" flash that the legacy
// activeTab + ?tab= URL hack caused (every tab change unmounted and
// remounted the entire workspace). Children render under the same shell
// instance now — `<Slot />` swaps only the body content.

import type { Href } from "expo-router";
import { COLORS } from "../../../../../lib/brand";
import {
  router,
  Slot,
  useFocusEffect,
  useLocalSearchParams,
  usePathname,
} from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, ScrollView, View } from "react-native";

import { StudentProgramShell, type ProgramTab } from "../../../../../components/student/StudentProgramShell";
import { type StudentPortalIdentity } from "../../../../../components/student/StudentPortalShell";
import { ProgramEntrance } from "../../../../../components/student/ProgramEntrance";
import { Button, Card, EmptyState } from "../../../../../components/ui";
import {
  CompetitionShellProvider,
  type CompetitionShellValue,
} from "../../../../../components/student/CompetitionShellContext";
import { listMyAnnouncements, type MyAnnouncement } from "../../../../../lib/announcements";
import { useAuth } from "../../../../../lib/authContext";
import { getCompetition } from "../../../../../lib/competitions";
import { readCachedCompetition, writeCachedCompetition } from "../../../../../lib/competitionCache";
import { getCompetitionLeaderboard } from "../../../../../lib/leaderboard";
import { listDeliverables, listDeliverablesForTeam } from "../../../../../lib/deliverables";
import { readCachedMyTeam, writeCachedMyTeam } from "../../../../../lib/myTeamCache";
import { isEligibleForCompetition, getMyRegistration } from "../../../../../lib/registrations";
import {
  getTeamDeliverableStatuses,
  listMyTeamSubmissions,
} from "../../../../../lib/submissions";
import { countRegisteredPrograms } from "../../../../../lib/studentDashboard";
import { firstNameFromDisplay, studentPortalRoleLine } from "../../../../../lib/studentPortalUi";
import { countActiveTeamsForStudent } from "../../../../../lib/studentTeams";
import { getMyStudent } from "../../../../../lib/students";
import { supabase } from "../../../../../lib/supabase";
import {
  getMyTeamUnreadCount,
  getTeamUnreadCount,
  subscribeToTeamUnreadSignals,
} from "../../../../../lib/teamMessages";
import {
  listMyTeam,
  listPendingJoinRequests,
  listTeamsForCompetition,
} from "../../../../../lib/teams";
import {
  getCachedShellSnapshot,
  updateCachedShellSnapshot,
} from "../../../../../lib/useStudentPortalShellData";
import type {
  CompetitionDetail,
  CompetitionTeamBrowseRow,
  Deliverable,
  LeaderboardRow,
  MyTeamPayload,
  PendingJoinRequest,
  Student,
  TeamDeliverableStatusRow,
  TeamSubmissionRow,
} from "../../../../../types/database";

function firstStr(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

export default function CompetitionShellLayout() {
  const params = useLocalSearchParams<{
    id: string;
    tab?: string | string[];
  }>();
  const id = firstStr(params.id) ?? "";
  const pathname = usePathname();
  const { user } = useAuth();

  // ────────── Pathname → activeTab ──────────
  // Drives the sidebar highlight + the "Find Team" vs "Team" label switch.
  const activeTab: ProgramTab = useMemo(() => {
    if (pathname.endsWith("/team")) return "team";
    if (pathname.endsWith("/deliverables")) return "deliverables";
    if (pathname.endsWith("/info")) return "info";
    return "dashboard";
  }, [pathname]);

  // ────────── Shared state (was index.tsx top-level state) ──────────
  const [row, setRow] = useState<CompetitionDetail | null | undefined>(() => {
    if (!user?.id || !id) return undefined;
    return readCachedCompetition(user.id, id) ?? undefined;
  });
  const [student, setStudent] = useState<Student | null>(null);
  const [casesBadgeCount, setCasesBadgeCount] = useState<number>(
    () => getCachedShellSnapshot(user?.id)?.casesBadgeCount ?? 0,
  );
  // Per-team unread for THIS comp's team — drives the program sidebar Team badge.
  const [thisTeamUnreadCount, setThisTeamUnreadCount] = useState<number>(0);

  // hasLoaded mirrors hasLoadedRef so effects can re-run on load completion.
  const hasLoadedRef = useRef(row !== undefined);
  const [hasLoaded, setHasLoaded] = useState(row !== undefined);
  const lastLoadIdRef = useRef<string | undefined>(
    row !== undefined && id ? id : undefined,
  );

  const [regStatus, setRegStatus] = useState<string | null>(null);
  const [myTeam, setMyTeam] = useState<MyTeamPayload | null>(null);
  // cachedTeamId gates hasJoinedTeam on the FIRST FRAME so the "Find Team"
  // label doesn't flash before listMyTeam resolves.
  const [cachedTeamId, setCachedTeamId] = useState<string | null>(() => {
    if (!user?.id || !id) return null;
    const cached = readCachedMyTeam(user.id, id);
    return cached?.teamId ?? null;
  });

  // Tab-shared mutation state — any tab calling refresh() sets/clears these
  // via context. Kept here so a failed action error stays visible even when
  // the user switches tabs while the toast/banner is up.
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // Tab-shared "list" state — fetched once by load() and shared via context.
  const [browseTeams, setBrowseTeams] = useState<CompetitionTeamBrowseRow[]>([]);
  const [pending, setPending] = useState<PendingJoinRequest[]>([]);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [teamSubmissions, setTeamSubmissions] = useState<TeamSubmissionRow[]>([]);
  const [teamStatuses, setTeamStatuses] = useState<TeamDeliverableStatusRow[]>([]);
  const [leaderboardRows, setLeaderboardRows] = useState<LeaderboardRow[]>([]);
  const [announcements, setAnnouncements] = useState<MyAnnouncement[]>([]);

  // Re-read the myTeam cache once user.id resolves — covers deep-link mounts.
  useEffect(() => {
    if (!user?.id || !id) return;
    if (cachedTeamId !== null) return;
    if (myTeam !== null) return;
    const cached = readCachedMyTeam(user.id, id);
    if (cached?.teamId) setCachedTeamId(cached.teamId);
  }, [user?.id, id, cachedTeamId, myTeam]);

  // Same for competition row cache.
  useEffect(() => {
    if (!user?.id || !id) return;
    if (row !== undefined) return;
    const cached = readCachedCompetition(user.id, id);
    if (!cached) return;
    setRow(cached);
    hasLoadedRef.current = true;
    setHasLoaded(true);
    lastLoadIdRef.current = id;
  }, [user?.id, id, row]);

  // ────────── Backwards-compat: ?tab=X → push to sibling route ──────────
  // Legacy URLs like /student/competition/[id]?tab=team need to land on the
  // new nested route. Fires whenever both `id` and `tab` are present. No
  // loop risk because the redirect target has no `?tab=`, so this effect
  // won't re-trigger after replace.
  const tabFromParams = firstStr(params.tab);
  useEffect(() => {
    if (!id || !tabFromParams) return;
    if (
      tabFromParams === "deliverables" ||
      tabFromParams === "team" ||
      tabFromParams === "info"
    ) {
      router.replace(`/student/competition/${id}/${tabFromParams}` as Href);
    }
  }, [id, tabFromParams]);

  // ────────── Shared data fetch ──────────
  const load = useCallback(async () => {
    try {
      if (id && lastLoadIdRef.current !== id) {
        hasLoadedRef.current = false;
        setHasLoaded(false);
        lastLoadIdRef.current = id;
      }
      if (!id) {
        setRow(null);
        return;
      }
      if (!hasLoadedRef.current) {
        setRow(undefined);
        setActionErr(null);
        setDeliverables([]);
        setTeamSubmissions([]);
        setTeamStatuses([]);
        setLeaderboardRows([]);
      }
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData.user?.id ?? null;
      const [c, s, reg, team, pub, dels, regCount, teamsCount] = await Promise.all([
        getCompetition(id),
        getMyStudent(),
        getMyRegistration(id),
        listMyTeam(id),
        listTeamsForCompetition(id),
        listDeliverables(id),
        uid ? countRegisteredPrograms(uid) : Promise.resolve(0),
        uid ? countActiveTeamsForStudent(uid) : Promise.resolve(0),
      ]);
      setCasesBadgeCount(regCount);
      const unread = await getMyTeamUnreadCount();
      if (uid) {
        updateCachedShellSnapshot(uid, {
          casesBadgeCount: regCount,
          teamsBadgeCount: teamsCount,
          teamsUnreadCount: unread,
        });
      }
      // This-competition team unread badge.
      if (team?.team.id) {
        const t = await getTeamUnreadCount(team.team.id);
        setThisTeamUnreadCount(t);
      } else {
        setThisTeamUnreadCount(0);
      }
      setRow(c);
      setStudent(s);
      setRegStatus(reg?.status ?? null);
      setMyTeam(team);
      if (user?.id) {
        writeCachedMyTeam(user.id, id, team ? { teamId: team.team.id } : null);
        setCachedTeamId(team ? team.team.id : null);
        if (c) writeCachedCompetition(user.id, id, c);
      }
      setBrowseTeams(pub.filter((t) => !t.is_locked));
      // Team-aware deliverables — server redacts gate-blocked rows. Fall back
      // to the public list when no team yet.
      if (team?.team.id) {
        try {
          const teamDels = await listDeliverablesForTeam(team.team.id);
          setDeliverables(teamDels.length > 0 ? teamDels : dels);
        } catch {
          setDeliverables(dels);
        }
      } else {
        setDeliverables(dels);
      }
      const subs = await listMyTeamSubmissions(id, team?.team.id ?? null);
      setTeamSubmissions(subs);
      if (team?.team.id) {
        try {
          const statuses = await getTeamDeliverableStatuses(team.team.id);
          setTeamStatuses(statuses);
        } catch {
          setTeamStatuses([]);
        }
      } else {
        setTeamStatuses([]);
      }
      if (team?.team.id) {
        const pr = await listPendingJoinRequests(team.team.id);
        setPending(pr);
      } else {
        setPending([]);
      }
      try {
        const board = await getCompetitionLeaderboard(id);
        setLeaderboardRows(board);
      } catch {
        setLeaderboardRows([]);
      }
      try {
        const all = await listMyAnnouncements();
        setAnnouncements(
          all.filter(
            (a) =>
              a.audience_kind === "competition" && a.audience_competition_id === id,
          ),
        );
      } catch {
        setAnnouncements([]);
      }
    } finally {
      hasLoadedRef.current = true;
      setHasLoaded(true);
    }
  }, [id, user?.id]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  // ────────── Realtime: this-team unread badge ──────────
  useEffect(() => {
    const teamId = myTeam?.team.id;
    if (!teamId) return;
    const unsub = subscribeToTeamUnreadSignals(() => {
      void getTeamUnreadCount(teamId).then(setThisTeamUnreadCount);
    });
    return () => unsub();
  }, [myTeam?.team.id]);

  // ────────── Chrome identity ──────────
  const authDisplay =
    (user?.user_metadata?.full_name as string | undefined)?.trim() ||
    user?.email?.split("@")[0] ||
    "Student";
  const topbarFirstNameChrome = firstNameFromDisplay(authDisplay);
  const portalIdentityChrome = useMemo<StudentPortalIdentity>(
    () => ({
      fullName: authDisplay,
      subtitleLine: studentPortalRoleLine(student),
    }),
    [authDisplay, student],
  );
  const avatarUrlChrome = (user?.user_metadata?.avatar_url as string | undefined) ?? null;

  // ────────── Derived ──────────
  const eligible = student && row ? isEligibleForCompetition(student, row) : false;
  const registered = regStatus === "registered" || regStatus === "submitted";
  const hasJoinedTeam = !!myTeam || cachedTeamId != null;

  // ────────── Tab nav ──────────
  const handleTabChange = useCallback(
    (next: ProgramTab) => {
      if (!id) return;
      const base = `/student/competition/${id}`;
      const target =
        next === "dashboard"
          ? base
          : next === "deliverables"
            ? `${base}/deliverables`
            : next === "team"
              ? `${base}/team`
              : `${base}/info`;
      router.push(target as Href);
    },
    [id],
  );

  // ────────── Context value ──────────
  const ctxValue = useMemo<CompetitionShellValue>(
    () => ({
      competitionId: id,
      competition: row,
      registered,
      hasLoaded,
      eligible,
      regStatus,
      myTeam,
      student,
      deliverables,
      teamSubmissions,
      teamStatuses,
      leaderboardRows,
      announcements,
      setAnnouncements,
      browseTeams,
      pending,
      busy,
      setBusy,
      actionErr,
      setActionErr,
      refresh: load,
    }),
    [
      id,
      row,
      registered,
      hasLoaded,
      eligible,
      regStatus,
      myTeam,
      student,
      deliverables,
      teamSubmissions,
      teamStatuses,
      leaderboardRows,
      announcements,
      browseTeams,
      pending,
      busy,
      actionErr,
      load,
    ],
  );

  // ────────── Render ──────────
  const topbarTitle =
    row && typeof row === "object" ? row.name : "Competition";

  return (
    <StudentProgramShell
      topbarTitle={topbarTitle}
      topbarFirstName={topbarFirstNameChrome}
      avatarName={authDisplay}
      avatarUrl={avatarUrlChrome}
      identity={portalIdentityChrome}
      casesBadgeCount={casesBadgeCount}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      hasJoinedTeam={hasJoinedTeam}
      teamUnreadCount={thisTeamUnreadCount}
      competitionId={id}
    >
      {row === undefined ? (
        <View className="flex-1 items-center justify-center py-24">
          <ActivityIndicator size="large" color={COLORS.FOREST.hex} />
        </View>
      ) : row === null ? (
        <ScrollView className="flex-1 px-4" contentContainerStyle={{ paddingVertical: 24 }}>
          <Card variant="light" padding="lg">
            <EmptyState
              title="Competition not found"
              action={
                <Button onPress={() => router.replace("/student" as Href)}>Back to home</Button>
              }
            />
          </Card>
        </ScrollView>
      ) : (
        <>
          <ProgramEntrance
            key={row.id}
            competitionId={row.id}
            programName={row.name}
            companyName={row.company_name ?? null}
          />
          <CompetitionShellProvider value={ctxValue}>
            <Slot />
          </CompetitionShellProvider>
        </>
      )}
    </StudentProgramShell>
  );
}

